import React, { useEffect, useMemo, useState } from 'react'
import NumberFormat from 'react-number-format'
import {
    Button,
    CircularProgress,
    IconButton,
    TableCell,
    TableRow,
    TextField,
    useTheme,
} from '@material-ui/core'
import { useSelector } from 'react-redux'
import {
    CellData,
    Container,
    HeadCell,
    RowData,
    SimpleTable,
} from '../../components'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { useAppDispatch, useCompany, useUser } from '../../hooks'
import {
    ApartmentVendor,
    Company,
    Invoice,
    ListVendor,
    ModelMap,
    WorkorderStatus,
} from '../../models'
import { getInvoiceList, RootState } from '../../store'
import {
    axiosInstance,
    isAccountManager,
    resolveRoute,
    toMMDDYYYY,
    VENDOR,
} from '../../helpers'
import { useStyles } from '../../styles'
import { useHistory, useLocation } from 'react-router'
import { MergeVendorsModal } from './MergeVendorsModal'
import {
    ConfigureEntrataDetails,
    EntrataGLInfo,
    EntrataVendor,
} from './ConfigureEntrataDetails'
import { toast } from 'react-toastify'
import { VendorSendInvoiceModal } from './VendorSendInvoiceModal/VendorSendInvoiceModal'
import { workorderContext } from '../../contexts'
import {
    clearVendorInvoiceStep,
    INVOICE_CODE_STEP,
    useVendorInvoice,
} from '../../contexts/useVendorInvoice'
import { VendorInvoiceCodeGate } from './VendorInvoiceCodeGate'
import { VendorInvoiceRequestActions } from './VendorInvoiceRequestActions'
import { Routes } from '../../navigation/config/routes'
import { typographyStyle } from '../../theme/typographyStyle'

interface CompanyInvoiceRow extends RowData {
    sent_by: CellData<string>
    sent_to: CellData<string>
    invoices: CellData<number>
    latest_date: CellData<Date>
    company: CellData<Company>
}

interface InvoiceRequestRow extends RowData {
    client: CellData<string>
    date: CellData<Date>
    total_cost: CellData<number>
    invoice: CellData<Invoice>
    company: CellData<Company>
}

interface GroupedInvoice {
    company: Company
    invoices: Invoice[]
    latestDate: Date
    sentBy: Company
    sentTo: Company
}

export const InvoiceHome = () => {
    const [searchValue, setSearchValue] = useState('')
    const [openMatchVendor, setOpenMatchVendor] = useState(false)
    const [isLoading, setIsLoading] = useState(true)

    const [apartmentVendors, setApartmentVendors] = useState<ApartmentVendor[]>(
        [],
    )
    const [openConfigureVendors, setOpenConfigureVendors] = useState(false)
    const [openSendInvoiceModal, setOpenSendInvoiceModel] = useState(false)

    const [entrataVendors, setEntrataVendors] = useState<EntrataVendor[]>([])
    const [entrataGLInfo, setEntrataGLInfo] = useState<EntrataGLInfo[]>([])

    const { getVendorList, vendorList } = useCompany()

    const { workspaceUser } = useUser()
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const classes = useStyles()
    const history = useHistory()
    const location = useLocation()

    const { getWorkorderList, workorderList } = workorderContext()
    const vendorInvoiceController = useVendorInvoice()

    // Allow ?reset=1 in URL to force code entry (for debugging)
    useEffect(() => {
        if (new URLSearchParams(location.search).get('reset') === '1') {
            clearVendorInvoiceStep()
            vendorInvoiceController.setStep(INVOICE_CODE_STEP)
        }
    }, [location.search])

    const invoiceList = useSelector(
        (state: RootState) => state.invoice.invoiceList,
    )

    const invoiceListWorkspaceId = useSelector(
        (state: RootState) => state.invoice.invoiceListWorkspaceId,
    )

    const currentWorkspaceId = workspaceUser?.active_workspace?.id

    const getVendorData = () => {
        dispatch(getInvoiceList({}, currentWorkspaceId)).finally(() =>
            setIsLoading(false),
        )
        getWorkorderList({
            params: {
                wo_status: WorkorderStatus.APPROVED,
                all_jobsites: true,
                schedule_active: true,
            },
        })
    }

    useEffect(() => {
        if (
            invoiceList != null &&
            invoiceListWorkspaceId === currentWorkspaceId
        ) {
            setIsLoading(false)
            return
        }

        setIsLoading(true)
        if (workspaceUser?.active_workspace.company_type === 'APARTMENT') {
            const vendorPromise = getVendorList({ params: { my_team: true } })
            const invoicePromise = dispatch(
                getInvoiceList({}, currentWorkspaceId),
            )
            const apartmentVendorPromise = axiosInstance.get(
                `company/apartment_vendors/`,
            )

            Promise.all([vendorPromise, invoicePromise, apartmentVendorPromise])
                .then((values) => {
                    setApartmentVendors(values[2].data)
                })
                .catch((e) => toast.error(e.response.data.message))
                .finally(() => {
                    setIsLoading(false)
                })
        } else {
            getVendorData()
        }
    }, [currentWorkspaceId])

    let key: 'apartment' | 'vendor' = 'apartment'
    if (workspaceUser) {
        if (workspaceUser.active_workspace.company_type === 'APARTMENT') {
            key = 'vendor'
        }
    }

    // Company map is a dictionary mapping company id to the index it can be found in the
    // groupedInvoices array
    const companyMap: { [cId: number]: number } = {}
    const groupedInvoices: GroupedInvoice[] = []

    invoiceList?.forEach((inv) => {
        const company = inv[key]

        if (companyMap[company.id] === undefined) {
            // This invoices company has not been encountered yet.
            // Add the company to the company map
            // add a grouped invoice in the grouped invoice array at the end
            companyMap[company.id] = groupedInvoices.length

            groupedInvoices[groupedInvoices.length] = {
                company: company,
                invoices: [],
                latestDate: new Date(inv.created_date),
                sentBy: inv.vendor,
                sentTo: inv.apartment,
            }
        }

        // Add the invoice to the grouped invoice invoice list
        const groupedInvoice = groupedInvoices[companyMap[company.id]]
        groupedInvoice.invoices.push(inv)

        // Set the latestDate to this invoices date if it is greater than the previous max
        const checkDate = new Date(inv.created_date)
        groupedInvoice.latestDate =
            checkDate.valueOf > groupedInvoice.latestDate.valueOf
                ? checkDate
                : groupedInvoice.latestDate
    })

    // Table setup
    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
    }

    const cellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        fontWeight: 400,
    }

    const headCells: HeadCell<CompanyInvoiceRow>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'sent_by',
            label: 'Sent By',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'sent_to',
            label: 'Sent To',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'invoices',
            label: 'Invoices',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'latest_date',
            label: 'Latest Invoice Sent',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'empty',
            label: '',
            style: headCellStyle,
        },
    ]

    const createRow = (row: GroupedInvoice): CompanyInvoiceRow => ({
        sent_by: {
            value: row.sentBy.name,
            sortValue: row.sentBy.name,
        },
        sent_to: {
            value: row.sentTo.name,
            sortValue: row.sentTo.name,
        },
        invoices: {
            value: row.invoices.length,
            sortValue: row.invoices.length,
        },
        latest_date: {
            value: row.latestDate,
            sortValue: row.latestDate.valueOf(),
        },
        company: {
            value: row.company,
            sortValue: -1,
        },
    })
    // End Table Setup

    const isEzfolioInvoicePage =
        location.pathname.replace(/\/+$/, '') ===
        Routes.organization.invoiceHome.replace(/\/+$/, '')

    const clickRow = (company: Company) => {
        const route = isEzfolioInvoicePage
            ? Routes.organization.invoiceList
            : Routes.shared.financial.invoiceList
        history.push(resolveRoute(route, ':id', company.id))
    }

    const pendingInvoiceRequests = useMemo(
        () =>
            (invoiceList ?? []).filter(
                (inv) =>
                    inv.apartment_initiated_status === 'PENDING' &&
                    inv.total_cost > 0,
            ),
        [invoiceList],
    )

    const requestHeadCells: HeadCell<InvoiceRequestRow>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'client',
            label: 'Client',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'date',
            label: 'Date',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'total_cost',
            label: 'Amount',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'invoice',
            label: 'Response',
            style: headCellStyle,
        },
    ]

    const createRequestRow = (invoice: Invoice): InvoiceRequestRow => ({
        client: {
            value: invoice.apartment.name,
            sortValue: invoice.apartment.name,
        },
        date: {
            value: new Date(invoice.created_date),
            sortValue: new Date(invoice.created_date).valueOf(),
        },
        total_cost: {
            value: invoice.total_cost,
            sortValue: invoice.total_cost,
        },
        invoice: {
            value: invoice,
            sortValue: invoice.id,
        },
        company: {
            value: invoice.apartment,
            sortValue: invoice.apartment.id,
        },
    })

    const vendorMap: ModelMap<ListVendor> = {}
    vendorList.forEach((vendor) => (vendorMap[vendor.id] = vendor))

    const isVendor = workspaceUser?.active_workspace?.company_type === VENDOR
    const forceCodeEntry =
        new URLSearchParams(location.search).get('reset') === '1'

    // While user type is loading, show loading to prevent flash of content for vendors
    if (!workspaceUser) {
        return (
            <Container flex={1} style={{ justifyContent: 'center' }}>
                <CircularProgress
                    style={{ alignSelf: 'center', justifySelf: 'center' }}
                    size={100}
                />
            </Container>
        )
    }

    // Vendors must enter invoice code before seeing the page (apartments table, etc.)
    // ?reset=1 forces code entry for debugging
    if (
        (isVendor || forceCodeEntry) &&
        vendorInvoiceController.step === INVOICE_CODE_STEP
    ) {
        return <VendorInvoiceCodeGate layout="fullPage" />
    }

    return (
        <Container
            direction="column"
            style={{ flex: 1, height: 'calc(100vh - 104px)' }}
        >
            <Container
                style={{
                    marginBottom: theme.spacing(1),
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.h4),
                        fontWeight: 700,
                    }}
                >
                    Invoicing
                </span>
                <span
                    style={{
                        ...typographyStyle(theme.typography.subtitle1),
                        fontWeight: 300,
                        marginLeft: theme.spacing(1),
                    }}
                >
                    {groupedInvoices.length} Clients
                </span>

                <div style={{ flex: 1 }} />

                {workspaceUser?.active_workspace.company_type === VENDOR && (
                    <>
                        <Button
                            variant="contained"
                            style={{
                                margin: theme.spacing(2, 2, 2, 0),
                                backgroundColor: theme.palette.primary.dark,
                                color: 'white',
                                textTransform: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                document
                                    .getElementById('invoice-requests-section')
                                    ?.scrollIntoView({ behavior: 'smooth' })
                            }}
                        >
                            {`Invoice Requests (${pendingInvoiceRequests.length})`}
                        </Button>
                        <Button
                            variant="contained"
                            style={{
                                margin: theme.spacing(2, 2, 2, 0),
                                backgroundColor: theme.palette.primary.dark,
                                color: 'white',
                                textTransform: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => setOpenSendInvoiceModel(true)}
                        >
                            {`Ready to Invoice (${workorderList?.length ?? 0})`}
                        </Button>
                    </>
                )}

                {workspaceUser?.active_workspace.entrata_integrated && (
                    <Button
                        variant="contained"
                        style={{
                            margin: theme.spacing(2, 2, 2, 0),
                            backgroundColor: theme.palette.primary.dark,
                            color: 'white',
                            textTransform: 'none',
                            cursor: 'pointer',
                        }}
                        onClick={() => setOpenConfigureVendors(true)}
                    >
                        Configure Vendors
                    </Button>
                )}

                {isAccountManager(workspaceUser) && (
                    <Button
                        variant="contained"
                        style={{
                            margin: theme.spacing(2, 2, 2, 0),
                            backgroundColor: '#8A8A8A',
                            color: 'white',
                            textTransform: 'none',
                            cursor: 'pointer',
                        }}
                        onClick={() => setOpenMatchVendor(true)}
                    >
                        Merge Vendors
                    </Button>
                )}

                {workspaceUser?.active_workspace.entrata_integrated && (
                    <Button
                        variant="contained"
                        style={{
                            margin: theme.spacing(2, 2, 2, 0),
                            backgroundColor: theme.palette.primary.dark,
                            color: 'white',
                            textTransform: 'none',
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            axiosInstance
                                .post('company/vendor/sync_entrata_vendors/')
                                .then((res) => {
                                    toast.success(res.data.message)
                                })
                                .catch((e) => {
                                    toast.error(e.response.data.message)
                                })
                        }}
                    >
                        Resync Vendors
                    </Button>
                )}

                <TextField
                    variant="outlined"
                    placeholder="Search Invoicing"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                />
            </Container>
            {isLoading ? (
                <Container flex={1} style={{ justifyContent: 'center' }}>
                    <CircularProgress
                        style={{ alignSelf: 'center', justifySelf: 'center' }}
                        size={100}
                    />
                </Container>
            ) : (
                <>
                    {isVendor && pendingInvoiceRequests.length > 0 && (
                        <Container
                            id="invoice-requests-section"
                            direction="column"
                            style={{
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                    margin: theme.spacing(1, 0),
                                }}
                            >
                                Invoice Requests
                            </span>
                            <SimpleTable<InvoiceRequestRow>
                                rows={pendingInvoiceRequests.map(
                                    createRequestRow,
                                )}
                                render={(row) => (
                                    <TableRow
                                        key={`INVOICE_REQUEST_${row.invoice.value.id}`}
                                        style={{ cursor: 'pointer' }}
                                        className={classes.hoverGrey300}
                                        onClick={() =>
                                            clickRow(row.company.value)
                                        }
                                    >
                                        <TableCell>
                                            <Container direction="column">
                                                <span>{row.client.value}</span>
                                                <span
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 400,
                                                        color:
                                                            theme.palette
                                                                .grey[500],
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    EZID {row.company.value.id}
                                                </span>
                                            </Container>
                                        </TableCell>
                                        <TableCell style={cellStyle}>
                                            {toMMDDYYYY(row.date.value)}
                                        </TableCell>
                                        <TableCell style={cellStyle}>
                                            <NumberFormat
                                                displayType="text"
                                                value={row.total_cost.value}
                                                prefix="$"
                                                thousandSeparator
                                                fixedDecimalScale
                                                decimalScale={2}
                                            />
                                        </TableCell>
                                        <TableCell style={cellStyle}>
                                            <VendorInvoiceRequestActions
                                                invoice={row.invoice.value}
                                                onUpdated={getVendorData}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}
                                orderByDefault="date"
                                headCells={requestHeadCells}
                            />
                        </Container>
                    )}
                    <SimpleTable<CompanyInvoiceRow>
                        filter={(r) =>
                            r.company.value.name
                                .toLocaleLowerCase()
                                .includes(searchValue.toLocaleLowerCase())
                        }
                        rows={groupedInvoices.map((group) => createRow(group))}
                        render={(row) => {
                            return (
                                <TableRow
                                    key={`INVOICE_COMPANY_${row.company.value.id}`}
                                    style={{ cursor: 'pointer' }}
                                    className={classes.hoverGrey300}
                                    onClick={() => clickRow(row.company.value)}
                                >
                                    <TableCell>
                                        <Container>
                                            <Container
                                                direction="column"
                                                style={{
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <span>{row.sent_by.value}</span>
                                                {key === 'vendor' && (
                                                    <span
                                                        style={{
                                                            fontSize: '0.75rem',
                                                            fontWeight: 400,
                                                            color:
                                                                theme.palette
                                                                    .grey[500],
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        EZID{' '}
                                                        {row.company.value.id}
                                                    </span>
                                                )}
                                            </Container>
                                            {vendorMap[row.company.value.id]
                                                ?.entrata_sync_enabled && (
                                                <Container
                                                    style={{
                                                        marginLeft: theme.spacing(
                                                            1,
                                                        ),
                                                        fontWeight: 400,
                                                        fontSize: 13,
                                                        color:
                                                            theme.palette
                                                                .darkGreen.main,
                                                    }}
                                                >
                                                    (Sync Ready)
                                                </Container>
                                            )}
                                        </Container>
                                    </TableCell>
                                    <TableCell>
                                        <Container
                                            direction="column"
                                            style={{ justifyContent: 'center' }}
                                        >
                                            <span>{row.sent_to.value}</span>
                                            {key === 'apartment' && (
                                                <span
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 400,
                                                        color:
                                                            theme.palette
                                                                .grey[500],
                                                        lineHeight: 1.2,
                                                    }}
                                                >
                                                    EZID {row.company.value.id}
                                                </span>
                                            )}
                                        </Container>
                                    </TableCell>
                                    <TableCell style={cellStyle}>
                                        {row.invoices.value}
                                    </TableCell>
                                    <TableCell style={cellStyle}>
                                        {toMMDDYYYY(row.latest_date.value)}
                                    </TableCell>
                                    <TableCell align="center" style={cellStyle}>
                                        <IconButton>
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )
                        }}
                        orderByDefault="latest_date"
                        headCells={headCells}
                    />
                </>
            )}

            <MergeVendorsModal
                open={openMatchVendor}
                handleClose={() => setOpenMatchVendor(false)}
                vendors={vendorList}
            />

            <ConfigureEntrataDetails
                open={openConfigureVendors}
                handleClose={() => setOpenConfigureVendors(false)}
                apartmentVendors={apartmentVendors}
                setApartmentVendors={setApartmentVendors}
                entrataVendors={entrataVendors}
                entrataGLInfo={entrataGLInfo}
            />

            <VendorSendInvoiceModal
                open={openSendInvoiceModal}
                theme={theme}
                handleClose={() => setOpenSendInvoiceModel(false)}
                refreshData={getVendorData}
            />
        </Container>
    )
}
